import CustomField from 'elements/field/CustomField'
import type { ComponentType } from 'react'
import React, { useMemo } from 'react'
import { SelectInput, useTranslate } from 'react-admin'
import { useFormState } from 'react-final-form'

export type BulkEditSelectChoice = { id: number | string; name: string }

export type BulkEditSelectCustomFieldProps = {
  disabled: boolean
  allowEmpty?: boolean
}

export type BulkEditSelectFieldSpec = {
  source: string
  labelOverride?: string
  renderField?: ComponentType<BulkEditSelectCustomFieldProps>
  allowEmpty?: boolean
}

type Props = {
  disabled?: boolean
  visibleFieldKeys?: string[] | null
  resource: string
  fieldSpecs: readonly BulkEditSelectFieldSpec[]
  getChoicesForValues: (values: Record<string, unknown>) => Record<string, BulkEditSelectChoice[]>
}

export const BulkEditSelectFormFields: React.FC<Props> = ({
  disabled = false,
  visibleFieldKeys = null,
  resource,
  fieldSpecs,
  getChoicesForValues,
}) => {
  const translate = useTranslate()
  const formState = useFormState<{ [k: string]: unknown }>()
  const values = formState.values

  const choicesBySource = useMemo(() => getChoicesForValues(values), [getChoicesForValues, values])

  const show = (source: string) => visibleFieldKeys == null || visibleFieldKeys.includes(source)

  return (
    <>
      {fieldSpecs.map((spec) => {
        if (!show(spec.source)) return null
        const Custom = spec.renderField
        if (Custom) {
          return <Custom key={spec.source} disabled={disabled} allowEmpty={spec.allowEmpty} />
        }
        const clearable = spec.allowEmpty !== false
        return (
          <CustomField
            key={spec.source}
            component={SelectInput}
            source={spec.source}
            name={spec.source}
            label={spec.labelOverride ?? `resources.${resource}.fields.${spec.source}`}
            choices={choicesBySource[spec.source] ?? []}
            translateChoice={false}
            disabled={disabled}
            allowEmpty={clearable}
            fullWidth={clearable}
            emptyText={clearable ? translate('ra.action.clear_input_value') : undefined}
          />
        )
      })}
    </>
  )
}
